import { useCallback, useEffect, useState } from 'react';

import {
  fetchSessionTimeline,
  type TimelineCard,
} from '../../games/playtest-sessions-data.js';
import { useShell } from '../../shell/use-shell.js';
import { usePlaytestEventLogTick } from './playtest-event-log-context.js';

// Hook that pulls the chronological timeline for a single session, with
// realtime invalidation on event_log inserts (so a fresh /events fires
// the timeline rerender without a manual refresh) and on shell-context's
// playtestSessionsVersion (which catches dispatch / submission updates
// since playtest_sessions.last_activity_at moves). The event_log
// subscription itself is owned by PlaytestEventLogProvider - the three-
// column layout mounts both the sessions-list and the session-detail
// panes simultaneously, so we can't open two channels with the same name.
export function useSessionTimeline(sessionId: string | null): {
  cards: TimelineCard[];
  loading: boolean;
  reload: () => Promise<void>;
} {
  const { playtestSessionsVersion } = useShell();
  const eventLogTick = usePlaytestEventLogTick();
  const [cards, setCards] = useState<TimelineCard[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async (): Promise<void> => {
    if (sessionId === null) {
      setCards([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const next = await fetchSessionTimeline(sessionId);
    setCards(next);
    setLoading(false);
  }, [sessionId]);

  useEffect(() => {
    void reload();
  }, [reload, playtestSessionsVersion, eventLogTick]);

  return { cards, loading, reload };
}
